import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Linkedin, Download, Loader2, AlertCircle, CheckCircle2,
  Cookie, Zap, Bot, Search, Copy, Clock, Trash2, ChevronDown,
  ChevronUp, Map, Rocket, UserCheck, Mail, Phone, ShieldCheck,
  Send, MessageSquarePlus, Activity, Pause, ExternalLink, Check,
} from 'lucide-react';
import { toast } from 'sonner';
import MessageCampaignDrawer from './linkedin_msg/MessageCampaignDrawer';
import { fmtEST } from '../../utils/dateUtils';
import { copyToClipboard } from '../../utils/clipboard';

const API = process.env.REACT_APP_BACKEND_URL;

const PROVIDERS = [
  { id: 'salesapi', name: 'Sales API', icon: Rocket, color: 'bg-[#0A66C2]', desc: 'Direct API calls — fastest, 25/page, no browser needed' },
  { id: 'playwright', name: 'Playwright', icon: Bot, color: 'bg-purple-500', desc: 'Self-hosted browser (free, requires cookies)' },
  { id: 'apify', name: 'Apify', icon: Zap, color: 'bg-green-500', desc: 'Custom actor on Apify cloud (~$0.004/run)' },
];

function loadHistory() {
  try {
    return JSON.parse(localStorage.getItem('lnk_history') || '[]');
  } catch { return []; }
}

function saveHistory(history) {
  localStorage.setItem('lnk_history', JSON.stringify(history.slice(0, 20)));
}

export default function LinkedinDataPage() {
  const [url, setUrl] = useState(() => localStorage.getItem('lnk_url') || '');
  const [provider, setProvider] = useState(() => localStorage.getItem('lnk_provider') || 'salesapi');
  // Cookies are now saved per-platform in MongoDB — no local cookie state needed
  const [maxResults, setMaxResults] = useState(() => parseInt(localStorage.getItem('lnk_max')) || 2500);
  const [startFrom, setStartFrom] = useState(() => parseInt(localStorage.getItem('lnk_start')) || 1);
  const [resolveUrls, setResolveUrls] = useState(() => localStorage.getItem('lnk_resolve') !== 'false');
  const [enrichContacts, setEnrichContacts] = useState(() => localStorage.getItem('lnk_enrich') !== 'false');
  const [verifyEmails, setVerifyEmails] = useState(() => localStorage.getItem('lnk_verify_emails') === 'true');
  const [linkedinPlatform, setLinkedinPlatform] = useState(() => localStorage.getItem('lnk_platform') || 'sales_navigator');
  const [cookieStatuses, setCookieStatuses] = useState({});
  const [cityBatchMode, setCityBatchMode] = useState(false);
  const [batchName, setBatchName] = useState('');
  const [minPopulation, setMinPopulation] = useState(10000);
  const [cityCount, setCityCount] = useState(0);
  const [batches, setBatches] = useState([]);
  const [batchMode, setBatchMode] = useState(false);
  const [batchStatus, setBatchStatus] = useState(null);
  const [runId, setRunId] = useState(null);
  const [status, setStatus] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creditsUsed, setCreditsUsed] = useState(0);
  const [history, setHistory] = useState(loadHistory);
  const [expandedRun, setExpandedRun] = useState(null);
  const pollRef = useRef(null);

  // Messaging state
  const [selectedUrls, setSelectedUrls] = useState(new Set());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerRecipients, setDrawerRecipients] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [expandedCampaign, setExpandedCampaign] = useState(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);
  useEffect(() => { localStorage.setItem('lnk_url', url); }, [url]);
  useEffect(() => { localStorage.setItem('lnk_max', String(maxResults)); }, [maxResults]);
  useEffect(() => { localStorage.setItem('lnk_start', String(startFrom)); }, [startFrom]);
  useEffect(() => { localStorage.setItem('lnk_resolve', String(resolveUrls)); }, [resolveUrls]);
  useEffect(() => { localStorage.setItem('lnk_enrich', String(enrichContacts)); }, [enrichContacts]);
  useEffect(() => { localStorage.setItem('lnk_verify_emails', String(verifyEmails)); }, [verifyEmails]);
  useEffect(() => { localStorage.setItem('lnk_provider', provider); }, [provider]);
  useEffect(() => { localStorage.setItem('lnk_platform', linkedinPlatform); }, [linkedinPlatform]);

  // Sales API only works with Sales Navigator — auto-swap to playwright on platform change
  useEffect(() => {
    if (provider === 'salesapi' && linkedinPlatform !== 'sales_navigator') {
      setProvider('playwright');
    }
  }, [linkedinPlatform, provider]);

  useEffect(() => {
    axios.get(`${API}/api/linkedin-data/cookies`, { withCredentials: true })
      .then(r => setCookieStatuses(r.data))
      .catch(() => {});
    axios.get(`${API}/api/linkedin-data/cities/stats`, { withCredentials: true })
      .then(r => setCityCount(r.data.total || 0))
      .catch(() => {});
    axios.get(`${API}/api/linkedin-data/batches`, { withCredentials: true })
      .then(r => setBatches(r.data || []))
      .catch(() => {});
    loadCampaigns();
  }, []);

  const loadCampaigns = () => {
    axios.get(`${API}/api/linkedin-msg/campaigns?limit=10`, { withCredentials: true })
      .then(r => setCampaigns(r.data?.campaigns || []))
      .catch(() => {});
  };

  // Auto-select all profiles on load
  useEffect(() => {
    if (profiles.length > 0 && selectedUrls.size === 0) {
      setSelectedUrls(new Set(profiles.map(p => p.profile_url || p.linkedin_url).filter(Boolean)));
    }
  }, [profiles]);

  const toggleSelect = (url) => {
    if (!url) return;
    const next = new Set(selectedUrls);
    if (next.has(url)) next.delete(url); else next.add(url);
    setSelectedUrls(next);
  };

  const toggleSelectAll = () => {
    if (selectedUrls.size === profiles.length) {
      setSelectedUrls(new Set());
    } else {
      setSelectedUrls(new Set(profiles.map(p => p.profile_url || p.linkedin_url).filter(Boolean)));
    }
  };

  const openMessageDrawer = (subsetProfiles) => {
    const picks = subsetProfiles && subsetProfiles.length > 0
      ? subsetProfiles
      : profiles.filter(p => selectedUrls.has(p.profile_url || p.linkedin_url));
    if (picks.length === 0) {
      toast.error('No profiles selected');
      return;
    }
    setDrawerRecipients(picks);
    setDrawerOpen(true);
  };

  const handleCampaignAction = async (campaignId, action) => {
    try {
      await axios.post(`${API}/api/linkedin-msg/campaigns/${campaignId}/${action}`, null, { withCredentials: true });
      toast.success(`Campaign ${action}d`);
      loadCampaigns();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${action}`);
    }
  };

  const addToHistory = (entry) => {
    const updated = [entry, ...history.filter(h => h.run_id !== entry.run_id)].slice(0, 20);
    setHistory(updated);
    saveHistory(updated);
  };

  const removeFromHistory = (rid) => {
    const updated = history.filter(h => h.run_id !== rid);
    setHistory(updated);
    saveHistory(updated);
    if (expandedRun === rid) setExpandedRun(null);
  };

  const clearHistory = () => {
    setHistory([]);
    saveHistory([]);
    setExpandedRun(null);
  };

  const startExtraction = async () => {
    const urlPatterns = {
      regular: 'linkedin.com/search/results/people',
      sales_navigator: 'linkedin.com/sales',
      recruiter: ['linkedin.com/recruiter', 'linkedin.com/talent'],
    };
    const patterns = urlPatterns[linkedinPlatform];
    const valid = Array.isArray(patterns) ? patterns.some(p => url.includes(p)) : url.includes(patterns);
    if (!valid) {
      setError(`URL must be a ${linkedinPlatform === 'regular' ? 'Regular LinkedIn' : linkedinPlatform === 'recruiter' ? 'Recruiter' : 'Sales Navigator'} search URL`);
      return;
    }

    setError(null);
    setProfiles([]);
    setStatus('pending');
    setLoading(true);
    setCreditsUsed(0);
    setBatchStatus(null);

    try {
      // Backend loads cookies from DB for the selected platform — no need to pass from frontend

      let data;
      // City batch mode
      if (cityBatchMode) {
        const resp = await axios.post(`${API}/api/linkedin-data/batch-extract-cities`, {
          url,
          platform: linkedinPlatform,
          provider,
          batch_name: batchName || `${linkedinPlatform}_${new Date().toISOString().slice(0,10).replace(/-/g, '')}`,
          max_per_city: maxResults,
          min_population: minPopulation,
          resolve_urls: resolveUrls,
          enrich_contacts: enrichContacts,
        }, { withCredentials: true });
        data = resp.data;
      } else if (batchMode) {
        const body = { url, provider, platform: linkedinPlatform, resolve_urls: resolveUrls, enrich_contacts: enrichContacts, max_per_state: maxResults };
        const resp = await axios.post(`${API}/api/linkedin-data/batch-extract`, body, { withCredentials: true });
        data = resp.data;
      } else {
        const body = { url, max_results: maxResults, start_from: startFrom, provider, platform: linkedinPlatform, resolve_urls: resolveUrls, enrich_contacts: enrichContacts, verify_emails: verifyEmails };
        const resp = await axios.post(`${API}/api/linkedin-data/extract`, body, { withCredentials: true });
        data = resp.data;
      }
      setRunId(data.run_id);
      setStatus('running');

      pollRef.current = setInterval(async () => {
        try {
          const { data: s } = await axios.get(`${API}/api/linkedin-data/status/${data.run_id}`, { withCredentials: true });
          setStatus(s.status);
          setBatchStatus(s);

          if (s.status === 'succeeded') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            const { data: r } = await axios.get(`${API}/api/linkedin-data/results/${data.run_id}`, { withCredentials: true });
            setProfiles(r.profiles);
            setCreditsUsed(r.credits_used);
            setLoading(false);
            addToHistory({
              run_id: data.run_id,
              provider,
              profile_count: r.profiles.length,
              profiles: r.profiles,
              timestamp: new Date().toISOString(),
              resolve_urls: resolveUrls,
              enrich_contacts: enrichContacts,
              credits_used: r.credits_used,
            });
          } else if (s.status === 'failed') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setError(s.error || 'Extraction failed');
            setLoading(false);
          }
        } catch {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setError('Lost connection to server');
          setLoading(false);
        }
      }, 5000);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to start extraction');
      setLoading(false);
      setStatus(null);
    }
  };

  const downloadCsv = () => {
    if (!runId) return;
    window.open(`${API}/api/linkedin-data/download/${runId}`, '_blank');
  };

  const downloadHistoryCsv = (entry) => {
    const hasContact = entry.profiles.some(p => p.email || p.phone);
    const hasVerify = entry.profiles.some(p => p.email_verify_status);
    const header = ['first_name', 'last_name', 'profile_url', 'linkedin_url'];
    if (hasContact) header.push('email', 'phone', 'twitter', 'websites');
    if (hasVerify) header.push('email_verify_status');
    const rows = [header];
    for (const p of entry.profiles) {
      const row = [p.first_name, p.last_name, p.profile_url || '', p.linkedin_url || ''];
      if (hasContact) row.push(p.email || '', p.phone || '', p.twitter || '', p.websites || '');
      if (hasVerify) row.push(p.email_verify_status || '');
      rows.push(row);
    }
    const csvStr = rows.map(r => r.map(c => `"${(c || '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csvStr], { type: 'text/csv' });
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `linkedin_profiles_${entry.run_id}.csv`;
    a.click();
    URL.revokeObjectURL(blobUrl);
  };

  const profileCount = batchStatus?.profile_count || 0;
  const statusLabel = {
    pending: 'Starting extraction...',
    running: batchStatus?.batch
      ? `[${batchStatus.batch_completed_states}/${batchStatus.batch_total_states}] ${batchStatus.batch_current_state} — ${profileCount} profiles scraped`
      : `Extracting via ${PROVIDERS.find(p => p.id === provider)?.name}... ${profileCount > 0 ? `(${profileCount} profiles scraped)` : ''}`,
    verifying: `Verifying emails... (${profileCount} profiles extracted)`,
    succeeded: `Complete - ${profiles.length} profiles found${batchStatus?.batch ? ` across ${batchStatus.batch_total_states} states` : ''}`,
    failed: 'Extraction failed',
  };

  return (
    <div className="max-w-[1600px] mx-auto py-8 px-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-[#0A66C2] rounded-lg p-2">
          <Linkedin className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">LNKD-Data</h1>
          <p className="text-sm text-gray-500">Extract profiles from LinkedIn Sales Navigator searches</p>
        </div>
      </div>

      <div className="flex gap-6">
        {/* LEFT: Controls + Current Results */}
        <div className="flex-1 min-w-0">
          {/* LinkedIn Platform */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">LinkedIn Platform</h3>
            <div className="grid grid-cols-3 gap-3">
              {[
                { id: 'regular', label: 'Regular LinkedIn', desc: 'linkedin.com/search' },
                { id: 'sales_navigator', label: 'Sales Navigator', desc: 'linkedin.com/sales' },
                { id: 'recruiter', label: 'Recruiter', desc: 'linkedin.com/talent' },
              ].map(p => {
                const status = cookieStatuses[p.id];
                const selected = linkedinPlatform === p.id;
                return (
                  <button key={p.id} type="button" onClick={() => setLinkedinPlatform(p.id)}
                    className={`relative text-left rounded-xl border-2 p-3 transition-all ${selected ? 'border-[#0A66C2] bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <p className="text-sm font-medium">{p.label}</p>
                    <p className="text-xs text-gray-500">{p.desc}</p>
                    {status?.saved && <span className="absolute top-2 right-2 w-2.5 h-2.5 bg-green-500 rounded-full" title="Cookies saved" />}
                    {selected && <div className="absolute top-2 right-8 w-2.5 h-2.5 bg-[#0A66C2] rounded-full" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Provider Selector */}
          <div className="grid grid-cols-3 gap-3 mb-3">
            {PROVIDERS.map((p) => {
              const Icon = p.icon;
              const selected = provider === p.id;
              // Sales API only works with Sales Navigator URLs
              const salesapiBlocked = p.id === 'salesapi' && linkedinPlatform !== 'sales_navigator';
              const isDisabled = loading || salesapiBlocked;
              const blockLabel = salesapiBlocked ? 'SN only' : null;
              const blockTitle = salesapiBlocked
                ? 'Sales API requires the Sales Navigator platform — switch platform above to enable'
                : undefined;
              return (
                <button
                  key={p.id}
                  onClick={() => !isDisabled && setProvider(p.id)}
                  disabled={isDisabled}
                  title={blockTitle}
                  className={`relative rounded-xl border-2 p-4 text-left transition-all ${
                    isDisabled && !selected
                      ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                      : selected
                      ? 'border-[#0A66C2] bg-blue-50 shadow-sm'
                      : 'border-gray-200 bg-white hover:border-gray-300 cursor-pointer'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`${p.color} rounded-md p-1`}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <span className="font-semibold text-sm text-gray-900">{p.name}</span>
                    {blockLabel && (
                      <span className="ml-auto text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full">{blockLabel}</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 leading-tight">{p.desc}</p>
                  {selected && <div className="absolute top-2 right-2 w-2.5 h-2.5 bg-[#0A66C2] rounded-full" />}
                </button>
              );
            })}
          </div>
          {/* Main Card */}
          <Card className="mb-6">
            <CardContent className="pt-6 space-y-5">
              {/* Cookie Management */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Cookie className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">LinkedIn Cookies</span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { id: 'regular', label: 'Regular LinkedIn' },
                    { id: 'sales_navigator', label: 'Sales Navigator' },
                    { id: 'recruiter', label: 'Recruiter' },
                  ].map(platform => {
                    const status = cookieStatuses[platform.id];
                    return (
                      <div key={platform.id} className="border rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium">{platform.label}</span>
                          {status?.saved ? (
                            <span className="text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded">Saved</span>
                          ) : (
                            <span className="text-xs text-gray-400">Not set</span>
                          )}
                        </div>
                        <textarea
                          placeholder='Paste cookie JSON...'
                          rows={3}
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs font-mono resize-y"
                          onBlur={async (e) => {
                            if (!e.target.value.trim()) return;
                            try {
                              const parsed = JSON.parse(e.target.value);
                              const cookies = parsed.cookies || parsed;
                              if (!Array.isArray(cookies)) { toast.error('Invalid format'); return; }
                              if (!cookies.find(c => c.name === 'li_at')) { toast.error('Missing li_at cookie'); return; }
                              await axios.put(`${API}/api/linkedin-data/cookies/${platform.id}`, { cookies_json: cookies }, { withCredentials: true });
                              toast.success(`${platform.label} cookies saved`);
                              e.target.value = '';
                              const r = await axios.get(`${API}/api/linkedin-data/cookies`, { withCredentials: true });
                              setCookieStatuses(r.data);
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Failed to save cookies');
                            }
                          }}
                        />
                        {status?.saved && (
                          <p className="text-xs text-gray-400 mt-1">li_at: {status.li_at_hint} ({status.cookie_count} cookies)</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* URL Input */}
              <div>
                <Label className="text-sm mb-1 block">
                  {linkedinPlatform === 'regular' ? 'LinkedIn People Search URL' :
                   linkedinPlatform === 'recruiter' ? 'LinkedIn Recruiter Search URL' :
                   'Sales Navigator Search URL'}
                </Label>
                <Input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder={linkedinPlatform === 'regular' ? 'https://www.linkedin.com/search/results/people?...' :
                    linkedinPlatform === 'recruiter' ? 'https://www.linkedin.com/talent/search?...' :
                    'https://www.linkedin.com/sales/search/people?...'}
                  className="h-11"
                  disabled={loading}
                />
              </div>

              {/* Start From + Max Results */}
              <div className="flex gap-4">
                <div>
                  <Label className="text-sm mb-1 block">Start From</Label>
                  <Input
                    type="number"
                    value={startFrom}
                    onChange={(e) => setStartFrom(Math.max(1, parseInt(e.target.value) || 1))}
                    min={1}
                    max={2500}
                    className="w-28"
                    disabled={loading}
                  />
                  <p className="text-xs text-gray-400 mt-1">Skip first N-1 profiles</p>
                </div>
                <div>
                  <Label className="text-sm mb-1 block">Max Results</Label>
                  <Input
                    type="number"
                    value={maxResults}
                    onChange={(e) => setMaxResults(Math.max(1, parseInt(e.target.value) || 2500))}
                    min={1}
                    max={2500}
                    className="w-28"
                    disabled={loading}
                  />
                  <p className="text-xs text-gray-400 mt-1">Profiles to extract</p>
                </div>
              </div>

              {/* Batch Mode + Resolve URLs */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={batchMode}
                    onChange={(e) => { setBatchMode(e.target.checked); if (e.target.checked) setCityBatchMode(false); }}
                    className="rounded border-gray-300 text-[#0A66C2] focus:ring-[#0A66C2]"
                    disabled={loading}
                  />
                  <Map className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-700">Batch by US State</span>
                  <span className="text-xs text-gray-400">(loops all 50 states + DC, humanized pauses)</span>
                </label>
                {batchMode && (
                  <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg ml-6">
                    Loops through all 50 states + DC. "Max Results" becomes max per state. URL must have a US geo filter. Estimated: 30-90 min.
                  </p>
                )}
                {/* City Batch Mode */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={cityBatchMode}
                    onChange={(e) => { setCityBatchMode(e.target.checked); if (e.target.checked) setBatchMode(false); }}
                    className="rounded border-gray-300 text-[#0A66C2] focus:ring-[#0A66C2]"
                    disabled={loading}
                  />
                  <span className="text-sm text-gray-600">Split by US cities ({cityCount.toLocaleString()} cities)</span>
                </label>

                {cityBatchMode && (
                  <div className="pl-6 space-y-3 mt-2">
                    <div>
                      <Label className="text-xs mb-1 block">Batch Name</Label>
                      <Input value={batchName} onChange={e => setBatchName(e.target.value)}
                        placeholder={`${linkedinPlatform}_${new Date().toISOString().slice(0,10)}`}
                        className="text-sm" />
                    </div>
                    <div>
                      <Label className="text-xs mb-1 block">Min Population</Label>
                      <Input type="number" value={minPopulation} onChange={e => setMinPopulation(parseInt(e.target.value) || 10000)}
                        className="text-sm w-32" />
                    </div>
                    <p className="text-xs text-gray-500">Results will be saved to database for later export.</p>
                  </div>
                )}

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={resolveUrls}
                    onChange={(e) => setResolveUrls(e.target.checked)}
                    className="rounded border-gray-300 text-[#0A66C2] focus:ring-[#0A66C2]"
                    disabled={loading}
                  />
                  <span className="text-sm text-gray-700">Resolve public profile URLs</span>
                  <span className="text-xs text-gray-400">(via Voyager API — adds ~0.5s per profile)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enrichContacts}
                    onChange={(e) => setEnrichContacts(e.target.checked)}
                    className="rounded border-gray-300 text-[#0A66C2] focus:ring-[#0A66C2]"
                    disabled={loading}
                  />
                  <UserCheck className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-700">Enrich contact info</span>
                  <span className="text-xs text-gray-400">(email, phone, twitter — 1st-degree only, ~0.3s/profile)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={verifyEmails}
                    onChange={(e) => setVerifyEmails(e.target.checked)}
                    className="rounded border-gray-300 text-[#0A66C2] focus:ring-[#0A66C2]"
                    disabled={loading}
                  />
                  <ShieldCheck className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-700">Verify Emails</span>
                  <span className="text-xs text-gray-400">(checks deliverability after extraction — adds email_verify_status to CSV)</span>
                </label>
              </div>

              {/* Extract Button */}
              <Button
                onClick={startExtraction}
                disabled={loading || !url}
                className="w-full h-11 gap-2 bg-[#0A66C2] hover:bg-blue-700 text-base"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" />{batchMode ? 'Batch extracting by state...' : `Extracting via ${PROVIDERS.find(p => p.id === provider)?.name}...`}</>
                ) : (
                  batchMode
                    ? `Batch Extract All 50 States via ${PROVIDERS.find(p => p.id === provider)?.name}`
                    : `Extract Profiles via ${PROVIDERS.find(p => p.id === provider)?.name}`
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Status */}
          {status && (
            <Card className={`mb-6 ${
              status === 'failed' ? 'border-red-200 bg-red-50' :
              status === 'succeeded' ? 'border-green-200 bg-green-50' :
              'border-blue-200 bg-blue-50'
            }`}>
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  {status === 'failed' ? (
                    <AlertCircle className="w-5 h-5 text-red-500 shrink-0" />
                  ) : status === 'succeeded' ? (
                    <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" />
                  ) : (
                    <Loader2 className="w-5 h-5 text-blue-500 animate-spin shrink-0" />
                  )}
                  <div>
                    <p className={`text-sm font-medium ${
                      status === 'failed' ? 'text-red-800' :
                      status === 'succeeded' ? 'text-green-800' :
                      'text-blue-800'
                    }`}>
                      {statusLabel[status]}
                    </p>
                    {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
                    {status === 'succeeded' && creditsUsed > 0 && (
                      <p className="text-xs text-green-600 mt-1">Cost: ${creditsUsed.toFixed(4)}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Error (standalone) */}
          {error && !status && (
            <Card className="border-red-200 bg-red-50 mb-6">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Results Table */}
          {profiles.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{profiles.length} profiles extracted
                    <span className="ml-2 text-xs font-normal text-gray-500">
                      {selectedUrls.size} selected
                    </span>
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => openMessageDrawer()}
                      disabled={selectedUrls.size === 0}
                      className="gap-2 bg-[#0A66C2] hover:bg-blue-700 text-white"
                    >
                      <Send className="w-4 h-4" />Message these ({selectedUrls.size})
                    </Button>
                    <Button onClick={downloadCsv} variant="outline" className="gap-2">
                      <Download className="w-4 h-4" />CSV
                    </Button>
                    {profiles[0]?.state && (
                      <Button
                        onClick={() => runId && window.open(`${API}/api/linkedin-data/download-zip/${runId}`, '_blank')}
                        variant="outline"
                        className="gap-2"
                      >
                        <Download className="w-4 h-4" />ZIP by State
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-3 px-4 font-medium text-gray-600 w-10">
                          <input type="checkbox"
                            checked={selectedUrls.size > 0 && selectedUrls.size === profiles.length}
                            onChange={toggleSelectAll}
                            className="rounded border-gray-300" />
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600 w-12">#</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">First Name</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Last Name</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Title</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Company</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Degree</th>
                        {profiles.some(p => p.city) && <th className="text-left py-3 px-4 font-medium text-gray-600">City</th>}
                        {profiles[0]?.state && <th className="text-left py-3 px-4 font-medium text-gray-600">State</th>}
                        {profiles.some(p => p.email) && <th className="text-left py-3 px-4 font-medium text-gray-600">Email</th>}
                        {profiles.some(p => p.phone) && <th className="text-left py-3 px-4 font-medium text-gray-600">Phone</th>}
                        {profiles.some(p => p.twitter) && <th className="text-left py-3 px-4 font-medium text-gray-600">Twitter</th>}
                        {profiles.some(p => p.websites) && <th className="text-left py-3 px-4 font-medium text-gray-600">Websites</th>}
                        <th className="text-left py-3 px-4 font-medium text-gray-600">LinkedIn URLs</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-600 w-28">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {profiles.map((p, i) => {
                        const purl = p.profile_url || p.linkedin_url;
                        const isSelected = purl && selectedUrls.has(purl);
                        return (
                        <tr key={i} className="border-b last:border-0 hover:bg-gray-50">
                          <td className="py-2.5 px-4">
                            <input type="checkbox" checked={isSelected}
                              onChange={() => toggleSelect(purl)}
                              disabled={!purl}
                              className="rounded border-gray-300" />
                          </td>
                          <td className="py-2.5 px-4 text-gray-400">{i + 1}</td>
                          <td className="py-2.5 px-4 text-gray-900">{p.first_name}</td>
                          <td className="py-2.5 px-4 text-gray-900">{p.last_name}</td>
                          <td className="py-2.5 px-4 text-gray-700 text-xs">{p.title || <span className="text-gray-300">—</span>}</td>
                          <td className="py-2.5 px-4 text-gray-700 text-xs">{p.company || <span className="text-gray-300">—</span>}</td>
                          <td className="py-2.5 px-4 text-xs">
                            {p.degree ? (
                              <span className={`px-2 py-0.5 rounded-full border text-[11px] font-medium ${
                                p.degree === '1st' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                p.degree === '2nd' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                'bg-gray-50 text-gray-700 border-gray-200'
                              }`}>{p.degree}</span>
                            ) : <span className="text-gray-300">—</span>}
                          </td>
                          {profiles.some(pr => pr.city) && <td className="py-2.5 px-4 text-gray-700 text-xs">{p.city || <span className="text-gray-300">—</span>}</td>}
                          {profiles[0]?.state && <td className="py-2.5 px-4 text-gray-600">{p.state}</td>}
                          {profiles.some(pr => pr.email) && (
                            <td className="py-2.5 px-4">
                              {p.email ? (
                                <div className="flex items-center gap-1">
                                  <Mail className="w-3 h-3 text-gray-400 shrink-0" />
                                  <span className="text-xs text-gray-700">{p.email}</span>
                                  <button onClick={() => copyToClipboard(p.email, 'Copied')} className="p-0.5 hover:bg-gray-100 rounded text-gray-300 hover:text-gray-600">
                                    <Copy className="w-3 h-3" />
                                  </button>
                                </div>
                              ) : <span className="text-gray-300">-</span>}
                            </td>
                          )}
                          {profiles.some(pr => pr.phone) && (
                            <td className="py-2.5 px-4">
                              {p.phone ? (
                                <div className="flex items-center gap-1">
                                  <Phone className="w-3 h-3 text-gray-400 shrink-0" />
                                  <span className="text-xs text-gray-700">{p.phone}</span>
                                </div>
                              ) : <span className="text-gray-300">-</span>}
                            </td>
                          )}
                          {profiles.some(pr => pr.twitter) && (
                            <td className="py-2.5 px-4">
                              {p.twitter ? (
                                <span className="text-xs text-gray-700">@{p.twitter}</span>
                              ) : <span className="text-gray-300">-</span>}
                            </td>
                          )}
                          {profiles.some(pr => pr.websites) && (
                            <td className="py-2.5 px-4">
                              {p.websites ? (
                                <span className="text-xs text-gray-700 break-all">{p.websites}</span>
                              ) : <span className="text-gray-300">-</span>}
                            </td>
                          )}
                          <td className="py-2.5 px-4">
                            {p.profile_url ? (
                              <div className="flex items-center gap-1.5">
                                <a
                                  href={p.profile_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[#0A66C2] hover:underline truncate max-w-[350px] flex items-center gap-1 text-xs"
                                >
                                  <Linkedin className="w-3.5 h-3.5 shrink-0" />
                                  {p.profile_url}
                                </a>
                                <button
                                  onClick={() => copyToClipboard(p.profile_url, 'Copied')}
                                  className="p-0.5 hover:bg-gray-100 rounded text-gray-300 hover:text-gray-600"
                                >
                                  <Copy className="w-3 h-3" />
                                </button>
                              </div>
                            ) : (
                              <span className="text-gray-300">-</span>
                            )}
                            {p.linkedin_url && (
                              <div className="mt-1">
                                <a
                                  href={p.linkedin_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-gray-400 hover:text-gray-600 hover:underline break-all"
                                  style={{ fontSize: '10px' }}
                                >
                                  {p.linkedin_url}
                                </a>
                              </div>
                            )}
                          </td>
                          <td className="py-2.5 px-4 text-right">
                            {purl ? (
                              <button
                                onClick={() => openMessageDrawer([p])}
                                className="inline-flex items-center gap-1 text-xs text-[#0A66C2] hover:bg-blue-50 px-2 py-1 rounded border border-blue-100"
                              >
                                <MessageSquarePlus className="w-3.5 h-3.5" /> Message
                              </button>
                            ) : <span className="text-gray-300">—</span>}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* RIGHT: Previous Results */}
        <div className="w-80 flex-shrink-0">
          <Card className="sticky top-8">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Previous Runs</span>
              </div>
              {history.length > 0 && (
                <button onClick={clearHistory} className="text-xs text-gray-400 hover:text-red-500">
                  Clear all
                </button>
              )}
            </div>

            {history.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <p className="text-sm text-gray-400">No previous runs</p>
              </div>
            ) : (
              <div className="max-h-[calc(100vh-140px)] overflow-y-auto">
                {history.map((entry) => {
                  const isExpanded = expandedRun === entry.run_id;
                  const providerInfo = PROVIDERS.find(p => p.id === entry.provider);
                  const Icon = providerInfo?.icon || Zap;
                  const timeStr = fmtEST(entry.timestamp);

                  return (
                    <div key={entry.run_id} className="border-b border-gray-100 last:border-0">
                      <div
                        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50"
                        onClick={() => setExpandedRun(isExpanded ? null : entry.run_id)}
                      >
                        <div className={`${providerInfo?.color || 'bg-gray-400'} rounded p-1 flex-shrink-0`}>
                          <Icon className="w-3 h-3 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-900">
                              {entry.profile_count} profiles
                            </span>
                            <span className="text-xs text-gray-400">{timeStr}</span>
                          </div>
                          <p className="text-xs text-gray-500 truncate">
                            {providerInfo?.name}{entry.resolve_urls ? ' + resolved' : ''}{entry.enrich_contacts ? ' + contacts' : ''}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const picks = (entry.profiles || []).filter(p => p.profile_url || p.linkedin_url);
                              if (picks.length === 0) return;
                              setDrawerRecipients(picks);
                              setDrawerOpen(true);
                            }}
                            title={`Message all ${entry.profile_count} profiles from this batch`}
                            className="p-1 rounded text-[#0A66C2] hover:bg-blue-50"
                          >
                            <Send className="w-3.5 h-3.5" />
                          </button>
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="px-4 pb-3">
                          <div className="flex gap-2 mb-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const picks = (entry.profiles || []).filter(p => p.profile_url || p.linkedin_url);
                                if (picks.length === 0) return;
                                setDrawerRecipients(picks);
                                setDrawerOpen(true);
                              }}
                              title={`Message all ${entry.profiles?.length || 0} profiles from this batch`}
                              className="flex items-center gap-1 text-xs bg-[#0A66C2] hover:bg-blue-700 text-white px-2 py-1 rounded"
                            >
                              <Send className="w-3 h-3" /> Message ({entry.profiles?.length || 0})
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); downloadHistoryCsv(entry); }}
                              className="flex items-center gap-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded"
                            >
                              <Download className="w-3 h-3" /> CSV
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); removeFromHistory(entry.run_id); }}
                              className="flex items-center gap-1 text-xs bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-600 px-2 py-1 rounded"
                            >
                              <Trash2 className="w-3 h-3" /> Remove
                            </button>
                          </div>
                          <div className="max-h-60 overflow-y-auto">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-gray-500">
                                  <th className="text-left py-1 pr-2">Name</th>
                                  <th className="text-left py-1">URL</th>
                                </tr>
                              </thead>
                              <tbody>
                                {entry.profiles.map((p, i) => (
                                  <tr key={i} className="border-t border-gray-50">
                                    <td className="py-1 pr-2 text-gray-900 whitespace-nowrap">
                                      {p.first_name} {p.last_name}
                                    </td>
                                    <td className="py-1">
                                      {p.profile_url ? (
                                        <a href={p.profile_url} target="_blank" rel="noopener noreferrer"
                                          className="text-[#0A66C2] hover:underline break-all">
                                          {p.profile_url.replace('https://www.linkedin.com/in/', '/in/')}
                                        </a>
                                      ) : (
                                        <span className="text-gray-300">-</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Message Campaigns sidebar */}
          <Card className="mt-6">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Send className="w-4 h-4 text-[#0A66C2]" />
                <span className="text-sm font-medium text-gray-700">Message Campaigns</span>
              </div>
              <a href="/admin/marketing/linkedin-activity"
                className="text-xs text-[#0A66C2] hover:underline inline-flex items-center gap-1">
                <Activity className="w-3 h-3" /> Activity
              </a>
            </div>
            {campaigns.length === 0 ? (
              <div className="px-4 py-6 text-center">
                <p className="text-sm text-gray-400">No campaigns yet</p>
                <p className="text-xs text-gray-400 mt-1">Select profiles → Message these</p>
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                {campaigns.map((c) => {
                  const isExpanded = expandedCampaign === c.id;
                  const pct = c.total_recipients > 0 ? Math.round((c.sent_count / c.total_recipients) * 100) : 0;
                  const dotColor =
                    c.status === 'running' ? 'bg-[#0A66C2]' :
                    c.status === 'paused' ? 'bg-amber-500' :
                    c.status === 'completed' ? 'bg-green-500' :
                    c.status === 'failed' ? 'bg-red-500' : 'bg-gray-400';
                  return (
                    <div key={c.id} className="border-b border-gray-100 last:border-0">
                      <div
                        onClick={() => setExpandedCampaign(isExpanded ? null : c.id)}
                        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50"
                      >
                        <div className={`${dotColor} rounded p-1 flex-shrink-0`}>
                          <Send className="w-3 h-3 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-900 truncate">{c.name}</span>
                            <span className="text-xs text-gray-400">{c.status}</span>
                          </div>
                          <p className="text-xs text-gray-500 truncate">
                            {c.sent_count}/{c.total_recipients} sent{c.replied_count > 0 ? ` · ${c.replied_count} replied` : ''}
                          </p>
                          <div className="mt-1 h-1 bg-gray-100 rounded">
                            <div className="h-full bg-[#0A66C2] rounded" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      </div>
                      {isExpanded && (
                        <div className="px-4 pb-3">
                          <div className="grid grid-cols-4 gap-2 mb-2 text-center">
                            <div className="rounded-md bg-gray-50 border border-gray-100 px-1 py-1">
                              <div className="text-[10px] uppercase tracking-wide text-gray-400">Sent</div>
                              <div className="text-sm font-semibold text-gray-900">{c.sent_count}</div>
                            </div>
                            <div className="rounded-md bg-green-50 border border-green-100 px-1 py-1">
                              <div className="text-[10px] uppercase tracking-wide text-green-700">Replied</div>
                              <div className="text-sm font-semibold text-green-800">{c.replied_count}</div>
                            </div>
                            <div className="rounded-md bg-amber-50 border border-amber-100 px-1 py-1">
                              <div className="text-[10px] uppercase tracking-wide text-amber-700">Skipped</div>
                              <div className="text-sm font-semibold text-amber-800">{c.skipped_count}</div>
                            </div>
                            <div className="rounded-md bg-red-50 border border-red-100 px-1 py-1">
                              <div className="text-[10px] uppercase tracking-wide text-red-600">Failed</div>
                              <div className="text-sm font-semibold text-red-700">{c.failed_count}</div>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            {c.status === 'running' && (
                              <button onClick={(e) => { e.stopPropagation(); handleCampaignAction(c.id, 'pause'); }}
                                className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded border border-gray-200 hover:bg-gray-50">
                                <Pause className="w-3 h-3" /> Pause
                              </button>
                            )}
                            {c.status === 'paused' && (
                              <button onClick={(e) => { e.stopPropagation(); handleCampaignAction(c.id, 'resume'); }}
                                className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded border border-gray-200 hover:bg-gray-50">
                                <Send className="w-3 h-3" /> Resume
                              </button>
                            )}
                            <a href={`/admin/marketing/linkedin-activity?campaign=${c.id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded border border-gray-200 hover:bg-gray-50">
                              <ExternalLink className="w-3 h-3" /> View
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {batches.length > 0 && (
            <Card className="mt-6">
              <CardContent className="pt-4">
                <h3 className="text-sm font-medium mb-3">City Batch History</h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {batches.map(b => (
                    <div key={b.batch_name || b.id} className="flex items-center justify-between text-xs border rounded-lg p-2">
                      <div>
                        <p className="font-medium">{b.batch_name}</p>
                        <p className="text-gray-400">{b.total_profiles?.toLocaleString()} profiles · {b.cities_completed}/{b.total_cities} cities</p>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => window.open(`${API}/api/linkedin-data/batches/${b.batch_name}/export?format=csv`, '_blank')}
                          className="text-blue-600 hover:underline">CSV</button>
                        <button onClick={() => window.open(`${API}/api/linkedin-data/batches/${b.batch_name}/export-by-city`, '_blank')}
                          className="text-blue-600 hover:underline">ZIP</button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <MessageCampaignDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onLaunched={() => { loadCampaigns(); }}
        selectedRecipients={drawerRecipients}
        platform={linkedinPlatform}
        searchUrl={url}
        extractionRunId={runId}
      />
    </div>
  );
}
